import { Avatar, Box, Grid, IconButton, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import dumyProfile from "../../assets/icons/dumyProfile.png";
import grayCameraIcon from "../../assets/icons/grayCameraIcon.png";
import AppLoader from "../../components/appLoader";
import CustomButton from "../../components/customButton";
import TextInput from "../../components/textInput";
import CustomTimePicker from "../../components/customTimePicker";
import { openSnackAlert, uploadMediaService } from "../../helper";
import { createWorker, updateWorkerById } from "../../services/Workers";
import CustomInputLabel from "../../components/customInputLabel";
import useWorkerStore from "../../zustand/useWorkerStore";

const AddEditEmployee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshWorkerData, allWorkers } = useWorkerStore();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    department: "",
    designation: "",
    password: "",
    profilePhoto: null,
    checkInTime: "",
    checkOutTime: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);

  // Check if we're in edit mode and fetch employee data
  useEffect(() => {
    const employeeIdFromState = location.state?.employeeId;
    if (employeeIdFromState) {
      setIsEditMode(true);
      setEmployeeId(employeeIdFromState);
      fetchEmployeeData(employeeIdFromState);
    }
  }, [location.state]);

  const fetchEmployeeData = (id) => {
    const employee = allWorkers.find(worker => worker._id === id);
    
    if (employee) {
      setFormData({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        email: employee.email || "",
        phone: employee.contact || "",
        department: employee.department || "",
        designation: employee.designation || "",
        password: "", // Don't pre-fill password for security
        profilePhoto: employee.profileImage || null,
        checkInTime: employee.checkInTime || "",
        checkOutTime: employee.checkOutTime || "",
      });
    } else {
      openSnackAlert("Employee not found", "error");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTimeChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeImage = async (event) => {
    if (event.target.files[0]) {
      try {
        setIsLoading(true);
        let uploadRes = await uploadMediaService(event.target.files[0]);
        setIsLoading(false);
        setFormData({
          ...formData,
          profilePhoto: uploadRes,
        });
        openSnackAlert("Media uploaded successfully", "success");
        event.target.value = "";
      } catch (error) {
        openSnackAlert(error, "error");
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.firstName)
      return openSnackAlert("First name is required", "error");
    else if (!formData.lastName)
      return openSnackAlert("Last name is required", "error");
    else if (!formData.email)
      return openSnackAlert("Email is required", "error");
    else if (!formData.phone)
      return openSnackAlert("Phone number is required", "error");
    else if (!formData.department)
      return openSnackAlert("Department is required", "error");
    else if (!formData.designation)
      return openSnackAlert("Designation is required", "error");
    else if (!isEditMode && !formData.password)
      return openSnackAlert("Password is required", "error");
    else if (!formData.checkInTime)
      return openSnackAlert("Check-in time is required", "error");
    else if (!formData.checkOutTime)
      return openSnackAlert("Check-out time is required", "error");
    else {
      try {
        setIsLoading(true);
        const payload = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          designation: formData.designation,
          department: formData.department,
          contact: formData.phone,
          checkInTime: formData.checkInTime,
          checkOutTime: formData.checkOutTime,
          ...(formData.profilePhoto && { profileImage: formData.profilePhoto }),
        };

        // Only include password for new employees
        if (!isEditMode && formData.password) {
          payload.password = formData.password;
        }

        let response;
        if (isEditMode) {
          response = await updateWorkerById(employeeId, payload);
        } else {
          response = await createWorker(payload);
        }
        
        const resData = response?.data;

        if (resData?.status === "success") {
          // Refresh the workers data after successful operation
          await refreshWorkerData();
          openSnackAlert(
            resData?.message || 
            (isEditMode ? "Employee updated successfully" : "Employee created successfully"), 
            "success"
          );
          navigate(-1);
        } else {
          openSnackAlert(
            resData?.message || 
            (isEditMode ? "Update employee failed" : "Create employee failed"), 
            "error"
          );
        }
      } catch (error) {
        setIsLoading(false);
        console.error(error);
        openSnackAlert(error?.message || "Something went wrong", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mt={0}
    >
      <AppLoader isVisible={isLoading} />
      <Typography variant="h4" fontWeight={600} mb={0.5}>
        {isEditMode ? "Edit Employee" : "Add Employee"}
      </Typography>
      <Typography variant="description">
        {isEditMode ? "Update employee information and settings" : "Admin-only control of employee access and profiles"}
      </Typography>

      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "left",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 90,
                height: 90,
                borderRadius: "50%",
                background: "#F2F4F7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
                position: "relative",
              }}
            >
              <Avatar
                src={formData?.profilePhoto || dumyProfile}
                alt="Profile"
                sx={{ width: 70, height: 70, bgcolor: "#eee" }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #eee",
                  p: 1,
                }}
                component="label"
              >
                <img src={grayCameraIcon} alt="Upload" width={12} />
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleChangeImage}
                />
              </IconButton>
            </Box>
            <Typography variant="caption" color="#222" fontWeight={500}>
              Upload Profile Photo
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={10}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextInput
                label="First Name"
                placeholder="Brandon"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                inputBgColor={"#fff"}
                borderColor={"#4CAF50"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                label="Last Name"
                placeholder="Doe"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                inputBgColor={"#fff"}
                borderColor={"#4CAF50"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                label="Email"
                placeholder="brandon.doe@email.com"
                name="email"
                value={formData.email}
                onChange={handleChange}
                inputBgColor={"#fff"}
                borderColor={"#4CAF50"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                label="Phone Number"
                placeholder="Enter Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                inputBgColor={"#fff"}
                borderColor={"#4CAF50"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                label="Department"
                placeholder="Enter Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                inputBgColor={"#fff"}
                borderColor={"#4CAF50"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                label="Designation"
                placeholder="Enter Role"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                inputBgColor={"#fff"}
                borderColor={"#4CAF50"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomInputLabel label="Check In Time" />
              <CustomTimePicker
                // label="Check In Time"
                placeholder="Select check-in time"
                value={formData.checkInTime}
                onChange={(value) => handleTimeChange("checkInTime", value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputLabel label="Check Out Time" />
              <CustomTimePicker
                // label="Check In Time"
                placeholder="Select check-out time"
                value={formData.checkOutTime}
                onChange={(value) => handleTimeChange("checkOutTime", value)}
                required
              />
            </Grid>
            {!isEditMode && (
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Set Password"
                  placeholder="Enter Password"
                  name="password"
                  type="password"
                  showPassIcon
                  value={formData.password}
                  onChange={handleChange}
                  inputBgColor={"#fff"}
                  borderColor={"#4CAF50"}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={6}>
        <CustomButton
          type="submit"
          btnLabel={isEditMode ? "Update" : "Save"}
          btnBgColor="#fff"
          btnTextColor="#225B27"
          borderColor="#225B27"
          isBorder="1px solid #225B27"
          width={100}
          sx={{ boxShadow: 0, borderRadius: "22px" }}
          handlePressBtn={handleSubmit}
        />
        <CustomButton
          btnLabel="Cancel"
          btnBgColor="#E0E0E0"
          btnTextColor="#222"
          width={100}
          sx={{ boxShadow: 0, borderRadius: "22px" }}
          onClick={() => {
            setFormData({
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              department: "",
              designation: "",
              password: "",
              profilePhoto: null,
              checkInTime: "",
              checkOutTime: "",
            });
          }}
        />
      </Box>
    </Box>
  );
};

export default AddEditEmployee;
